/**
 * Cartoon sky art: the sun (cel disc, ray ring, glint), the crescent moon (cel tones, craters),
 * sparkle stars, the round lobe used to build clouds, and the motion of the sun and moon
 * (squash and stretch, the ray spin).
 *
 * The sun and moon are the one place in the game drawn WITHOUT the dark ink outline: they are
 * light sources, and a near-black ring round them read heavy against the sky (player
 * feedback). They keep the cel recipe instead: a light rim on the upper left, the body, a
 * shade crescent on the lower right, and a thin edge in a deeper tone of their own colour
 * (amber for the sun, blue-grey for the moon) on the shade side only, fading out toward the
 * light. Neither body has a face.
 *
 * Pictures are drawn in code into small canvases, once per radius bucket and device pixel
 * ratio. Both sky renderers place the same canvases (Canvas 2D with drawImage, Pixi as
 * sprites), so the two paths cannot drift apart. Motion is a pure function of the
 * pause-aware wobble clock: a held clock gives a still frame, which the static-sky snapshot
 * cache relies on.
 */
import { sparklePath, TAU } from "./toon-kit.js";

/** Sun disc tones, midday. `edge` is the warm line on the shade side (no dark ink on the sun). */
export var SUN_TONES = {
  base: "#ffc93a",
  light: "#fff3a8",
  shade: "#f9a526",
  edge: "#ee8419",
};
/** Sun disc tones near the horizon at sunset (cross-faded in by `sunsetAmount`). */
export var SUN_SUNSET_TONES = {
  base: "#ffa13a",
  light: "#ffdc92",
  shade: "#f27434",
  edge: "#dd4f22",
};
/** Ray ring tones: two flat warm tones (a lighter stripe down each ray), no outline. */
export var RAY_TONES = { base: "#ffb11f", light: "#ffe27a" };
export var RAY_SUNSET_TONES = { base: "#ff8a36", light: "#ffc27a" };
/**
 * Moon crescent tones: cool cel whites and blue-greys. `edge` is the cool line on the shade
 * side and along the terminator (instead of near-black ink).
 */
export var MOON_TONES = {
  base: "#e8efff",
  light: "#ffffff",
  shade: "#aebfe6",
  crater: "#c3d0ef",
  craterShade: "#97aad6",
  edge: "#8596c8",
};

/**
 * The shade-side edge, in units of the body radius: the disc minus a slightly smaller disc
 * nudged toward the light (`dx`, `dy`) leaves a crescent that is `inset + |shift|` thick at the
 * lower right and runs out to nothing a little past the sides, so the rim facing the light
 * stays clean.
 */
export var CELESTIAL_EDGE = { dx: -0.04, dy: -0.05, inset: 0.012 };

/**
 * Cloud tone sets ([r,g,b]): ink (a darker tone of the cloud's own colour, as vapour takes),
 * shade, base and the light on the top lobes. Day white ↔ night slate; golden-hour washes for
 * sunrise and sunset. The day, night and dusk lines are kept a step lighter than a full ink,
 * so the clouds that drift in front of the un-inked sun and moon do not out-weigh them.
 */
export var CLOUD_TONES = {
  day: { ink: [106, 148, 194], shade: [186, 213, 242], base: [236, 245, 255], light: [255, 255, 255] },
  night: { ink: [30, 44, 90], shade: [50, 68, 120], base: [80, 100, 154], light: [116, 138, 194] },
  sunrise: { ink: [150, 98, 92], shade: [246, 178, 150], base: [255, 222, 194], light: [255, 245, 226] },
  sunset: { ink: [118, 58, 92], shade: [226, 120, 136], base: [255, 182, 160], light: [255, 222, 196] },
  // Golden-hour clouds on a darkening sky (after sunset, before sunrise): lilac and rose.
  dusk: { ink: [76, 52, 112], shade: [118, 86, 156], base: [178, 132, 186], light: [238, 178, 204] },
};

/**
 * Flat halo discs behind the bodies, innermost first: [radius × r, alpha]. Banded (cel) glow,
 * soft light only on emitters. The sun's inner band sits between the ray roots and tips, a warm
 * glow round the disc now that no ink ring separates it from the sky.
 */
export var SUN_HALO = [[1.26, 0.3], [1.8, 0.16], [2.45, 0.07]];
export var MOON_HALO = [[1.6, 0.09], [2.2, 0.045]];
/** Most halo bands either body uses (the Pixi layer keeps this many disc sprites). */
export var CELESTIAL_HALO_MAX = 3;
export var SUN_HALO_RGB = [255, 244, 186];
export var SUN_SUNSET_HALO_RGB = [255, 206, 150];
export var MOON_HALO_RGB = [214, 228, 255];

/**
 * Crescent geometry, in units of the moon radius: the shadow disc that bites the lit
 * crescent out of the full disc. The dark side (inside the bite) is drawn separately in the
 * colour of the sky behind it, so the moon always reads as a whole disc.
 */
export var MOON_BITE = { x: 0.66, y: -0.16, r: 0.8 };

/** Half-extent of each baked picture, in units of the body radius. */
var EXTENT = {
  sun: 1.12,
  sunSet: 1.12,
  rays: 1.64,
  raysSet: 1.64,
  moon: 1.12,
};

/** Weight of the moon's terminator line as a share of its radius. */
var MOON_TERMINATOR_LINE = 0.04;

// ---------------------------------------------------------------------------------------------
// Motion
// ---------------------------------------------------------------------------------------------

/** How tall the wobble may make the body (it peaks just under 1.09): visibility gates use r × this. */
export var CELESTIAL_MAX_STRETCH = 1.1;

/**
 * Squash and stretch for the sun or moon at a moment of the wobble clock: an endless
 * anti-phase wobble (squeezed vertically, then horizontally), volume-keeping. It runs the same
 * everywhere on the arc, the horizon included.
 *
 * @param {"sun"|"moon"} kind
 * @param {number} wobbleSec pause-aware wobble clock, seconds
 * @returns {{ sx: number, sy: number }}
 */
export function celestialSquash(kind, wobbleSec) {
  var t = Number(wobbleSec) || 0;
  var moon = kind === "moon";
  var w = moon ? 2.35 : 2.85;
  var amp = moon ? 0.065 : 0.075;
  var phase = moon ? 1.9 : 0;
  var wave = Math.sin(t * w + phase) + 0.22 * Math.sin(t * w * 2.31 + phase * 0.5);
  var sx = 1 + amp * wave / 1.22;
  return { sx: sx, sy: 1 / sx };
}

/** Slow spin of the ray ring (radians). */
export function sunRayAngle(wobbleSec) {
  return (Number(wobbleSec) || 0) * 0.16;
}

/** Ray ring breathing (scale around 1), a little out of step with the body wobble. */
export function sunRayPulse(wobbleSec) {
  return 1 + 0.035 * Math.sin((Number(wobbleSec) || 0) * 1.7 + 0.8);
}

/**
 * How far into the warm sunset look the sun is (0 midday → 1 at the horizon at sunset).
 * @param {number} colorPhase 0..1 sky colour phase (0.5 = sunset)
 */
export function sunsetAmount(colorPhase) {
  var t = ((Number(colorPhase) || 0) % 1 + 1) % 1;
  if (t >= 0.3 && t <= 0.62) return smooth01((t - 0.3) / 0.18);
  if (t >= 0.92 || t <= 0.08) {
    var d = t >= 0.92 ? t - 1 : t;
    return 0.75 * (1 - smooth01((d + 0.08) / 0.16));
  }
  return 0;
}

// ---------------------------------------------------------------------------------------------
// Baking
// ---------------------------------------------------------------------------------------------

/** Baked records per kind, keyed by the device radius bucket: { canvas, half, key, r, rpx }. */
var BAKES = newBakeTable();
var bakeCount = 0;
var drawable = null;

function newBakeTable() {
  var t = Object.create(null);
  for (var k in EXTENT) t[k] = Object.create(null);
  return t;
}

function canDraw() {
  if (typeof document === "undefined") return false;
  try {
    var probe = document.createElement("canvas");
    return !!(probe && probe.getContext && probe.getContext("2d"));
  } catch (_e) {
    return false;
  }
}

function newCanvas(px) {
  var cv = document.createElement("canvas");
  cv.width = px;
  cv.height = px;
  return cv;
}

/**
 * A baked sky picture for a body of radius `r` (CSS px) at `dpr`, or null without a 2D canvas.
 * The canvas is square and centred on the body; `half` is its half-size in CSS px.
 * Every call with the same bucket returns the same canvas object (Pixi textures are memoised
 * per canvas, so a canvas is never redrawn after it is handed out). The record itself is
 * cached too and only its `half` is refreshed, so the per-frame lookups allocate nothing;
 * read it right away, do not keep it.
 *
 * @param {"sun"|"sunSet"|"rays"|"raysSet"|"moon"} kind
 * @param {number} r body radius, CSS px
 * @param {number} [dpr]
 * @returns {{ canvas: HTMLCanvasElement, half: number, key: string }|null}
 */
export function getCelestialArt(kind, r, dpr) {
  var ext = EXTENT[kind];
  if (!ext || !(r > 0)) return null;
  var d = dpr > 0 ? Math.min(dpr, 3) : 1;
  // Bucket the device radius so a resize by a pixel or two reuses the bake.
  var rpx = Math.max(4, Math.round(r * d / 2) * 2);
  var hit = BAKES[kind][rpx];
  if (hit) {
    if (hit.r !== r) {
      hit.r = r;
      hit.half = (hit.canvas.width / 2) / rpx * r;
    }
    return hit;
  }
  if (drawable == null) drawable = canDraw();
  if (!drawable) return null;
  var size = Math.ceil(ext * rpx * 2 + 4);
  var cv = newCanvas(size);
  var c = cv.getContext("2d");
  c.translate(size / 2, size / 2);
  paintCelestial(c, kind, rpx);
  if (++bakeCount > 40) {
    BAKES = newBakeTable();
    bakeCount = 1;
  }
  var rec = { canvas: cv, half: (size / 2) / rpx * r, key: kind + "|" + rpx, r: r, rpx: rpx };
  BAKES[kind][rpx] = rec;
  return rec;
}

function paintCelestial(c, kind, R) {
  if (kind === "sun") drawSunDisc(c, R, SUN_TONES);
  else if (kind === "sunSet") drawSunDisc(c, R, SUN_SUNSET_TONES);
  else if (kind === "rays") drawSunRays(c, R, RAY_TONES);
  else if (kind === "raysSet") drawSunRays(c, R, RAY_SUNSET_TONES);
  else if (kind === "moon") drawMoonCrescent(c, R);
}

var shapeBakes = Object.create(null);

/**
 * A plain white disc (antialiased edge), used for cloud lobes, halos and the moon's dark side
 * in the Pixi layer, where it is tinted per use.
 * @returns {HTMLCanvasElement|null}
 */
export function getLobeDiscCanvas() {
  return bakeShape("disc", 256, function (c, s) {
    c.fillStyle = "#ffffff";
    c.beginPath();
    c.arc(s / 2, s / 2, s / 2 - 2, 0, TAU);
    c.fill();
  });
}

/** Radius of the white disc inside `getLobeDiscCanvas()`, as a share of the canvas half-size. */
export var LOBE_DISC_FILL = 126 / 128;

/**
 * A four-point sparkle star (or a round dot) for the star field: white for Pixi, which tints
 * each sprite, or painted in `rgb` for Canvas 2D, which cannot tint a drawImage.
 * @param {"sparkle"|"dot"} kind
 * @param {number[]} [rgb]
 */
export function getStarCanvas(kind, rgb) {
  if (rgb && kind === "sparkle") {
    var fill = "rgb(" + rgb[0] + "," + rgb[1] + "," + rgb[2] + ")";
    return bakeShape("sparkle|" + fill, 96, function (c, s) {
      paintSparkle(c, s / 2, s / 2, s / 2 - 1, fill);
    });
  }
  if (kind === "dot") {
    return bakeShape("dot", 32, function (c, s) {
      c.fillStyle = "#ffffff";
      c.beginPath();
      c.arc(s / 2, s / 2, s / 2 - 1, 0, TAU);
      c.fill();
    });
  }
  return bakeShape("sparkle", 96, function (c, s) {
    paintSparkle(c, s / 2, s / 2, s / 2 - 1);
  });
}

/**
 * A shooting star's tail: a flat tapered wedge (pale blue, with a cream core), head on the
 * right edge, point on the left. Drawn stretched to the tail's length.
 */
export function getStreakCanvas() {
  return bakeShape("streak", 256, function (c, s) {
    var h = s / 8;
    c.translate(0, (s - h) / 2);
    c.fillStyle = "rgba(190, 214, 255, 0.6)";
    c.beginPath();
    c.moveTo(s, 0);
    c.lineTo(0, h / 2);
    c.lineTo(s, h);
    c.closePath();
    c.fill();
    c.fillStyle = "rgba(255, 250, 226, 0.95)";
    c.beginPath();
    c.moveTo(s, h * 0.28);
    c.lineTo(s * 0.4, h / 2);
    c.lineTo(s, h * 0.72);
    c.closePath();
    c.fill();
  });
}

/** Height of the streak band inside its square canvas, as a share of the canvas size. */
export var STREAK_BAND = 1 / 8;

/** Share of the sparkle canvas half-size that the sparkle points reach. */
export var STAR_SPRITE_FILL = 47 / 48;

function bakeShape(key, size, draw) {
  if (key in shapeBakes) return shapeBakes[key];
  if (drawable == null) drawable = canDraw();
  if (!drawable) {
    shapeBakes[key] = null;
    return null;
  }
  var cv = newCanvas(size);
  draw(cv.getContext("2d"), size);
  shapeBakes[key] = cv;
  return cv;
}

/**
 * A crisp four-point sparkle with a round heart, centred on (x, y), points at radius `r`
 * (white unless `fill` is given).
 */
export function paintSparkle(c, x, y, r, fill) {
  c.fillStyle = fill || "#ffffff";
  c.beginPath();
  sparklePath(c, x, y, r, 0);
  c.fill();
  c.beginPath();
  c.arc(x, y, r * 0.2, 0, TAU);
  c.fill();
}

// ---------------------------------------------------------------------------------------------
// Pictures
// ---------------------------------------------------------------------------------------------

/**
 * The sun disc at the origin, radius R: a light rim on the upper left, the gold body, a shade
 * crescent on the lower right (nested offset circles, no gradients), a warm amber edge on the
 * shade side only and a glint. No dark outline: the sun is a light source, and the
 * sky round it, the halo bands and the rays already frame it.
 */
export function drawSunDisc(c, R, tones) {
  var T = tones || SUN_TONES;
  c.save();
  c.beginPath();
  c.arc(0, 0, R, 0, TAU);
  c.fillStyle = T.light;
  c.fill();
  c.clip();
  c.beginPath();
  c.arc(R * 0.06, R * 0.07, R * 0.94, 0, TAU);
  c.fillStyle = T.shade;
  c.fill();
  c.beginPath();
  c.arc(-R * 0.045, -R * 0.06, R * 0.79, 0, TAU);
  c.fillStyle = T.base;
  c.fill();
  fillShadeEdge(c, R, T.edge);
  c.restore();

  // Glint: a rotated white lozenge and a dot, up and to the left.
  c.save();
  c.fillStyle = "rgba(255,255,255,0.95)";
  c.beginPath();
  c.ellipse(-R * 0.46, -R * 0.42, R * 0.15, R * 0.085, -0.75, 0, TAU);
  c.fill();
  c.beginPath();
  c.arc(-R * 0.25, -R * 0.62, R * 0.05, 0, TAU);
  c.fill();
  c.restore();
}

/**
 * The shade-side edge inside the current clip (the body's silhouette): everything outside a
 * disc a hair smaller than the body and nudged toward the light, i.e. a crescent that is
 * thickest at the lower right and runs out before the lit upper left.
 */
function fillShadeEdge(c, R, color) {
  var e = CELESTIAL_EDGE;
  c.beginPath();
  c.rect(-R * 2, -R * 2, R * 4, R * 4);
  c.arc(R * e.dx, R * e.dy, R * (1 - e.inset), 0, TAU, true);
  c.fillStyle = color;
  c.fill("evenodd");
}

/**
 * The ray ring at the origin for a disc of radius R: twelve rounded rays, long and short in
 * turn, in two flat warm tones (the body and a lighter stripe down the middle), no outline.
 * The disc covers their roots.
 */
export function drawSunRays(c, R, tones) {
  var T = tones || RAY_TONES;
  var n = 12;
  c.save();
  // One path for all rays: union fill, then the stripes.
  c.beginPath();
  for (var i = 0; i < n; i++) rayPath(c, R, i, n, 1);
  c.fillStyle = T.base;
  c.fill();
  c.beginPath();
  for (var j = 0; j < n; j++) rayPath(c, R, j, n, 0.42);
  c.fillStyle = T.light;
  c.fill();
  c.restore();
}

function rayPath(c, R, i, n, widthK) {
  var a = (i / n) * TAU;
  var long = i % 2 === 0;
  var tip = R * (long ? 1.5 : 1.3);
  var root = R * 0.9;
  // A touch wider than the inked rays were: the ink line used to add that width.
  var half = (Math.PI / n) * (long ? 0.68 : 0.55) * widthK;
  var ca = Math.cos(a);
  var sa = Math.sin(a);
  function pt(rad, ang) {
    return [Math.cos(a + ang) * rad, Math.sin(a + ang) * rad];
  }
  var l = pt(root, -half);
  var rr = pt(root, half);
  var tipR = R * (long ? 0.095 : 0.08) * Math.max(0.5, widthK);
  var tipC = [ca * (tip - tipR), sa * (tip - tipR)];
  var nx = -sa;
  var ny = ca;
  c.moveTo(l[0], l[1]);
  c.lineTo(tipC[0] - nx * tipR, tipC[1] - ny * tipR);
  c.arc(tipC[0], tipC[1], tipR, a - Math.PI / 2, a + Math.PI / 2);
  c.lineTo(rr[0], rr[1]);
  c.closePath();
}

/**
 * The lit crescent of the moon at the origin, radius R: cel tones (light rim, body, shade
 * along the lower right), a blue-grey edge on the shade side, a soft terminator band with a
 * thin blue-grey line where it meets the dark side and cel craters. No dark outline. The
 * bitten-out dark side is not drawn here (see `drawMoonDarkSide`).
 */
export function drawMoonCrescent(c, R) {
  var T = MOON_TONES;
  var bx = MOON_BITE.x * R;
  var by = MOON_BITE.y * R;
  var br = MOON_BITE.r * R;

  c.save();
  clipCrescent(c, R, bx, by, br);
  c.fillStyle = T.light;
  c.fillRect(-R * 1.2, -R * 1.2, R * 2.4, R * 2.4);
  c.beginPath();
  c.arc(R * 0.05, R * 0.07, R * 0.95, 0, TAU);
  c.fillStyle = T.shade;
  c.fill();
  c.beginPath();
  c.arc(-R * 0.04, -R * 0.05, R * 0.82, 0, TAU);
  c.fillStyle = T.base;
  c.fill();
  // The terminator gets its own shade band, so the crescent reads as a ball in profile.
  c.beginPath();
  c.arc(bx - R * 0.09, by + R * 0.05, br + R * 0.1, 0, TAU);
  c.fillStyle = T.shade;
  c.globalAlpha = 0.55;
  c.fill();
  c.globalAlpha = 1;
  fillShadeEdge(c, R, T.edge);

  // Craters: a cool shade ring with a lighter floor nudged toward the shade, no outline.
  var craters = [[-0.6, -0.5, 0.12, 0.09], [-0.2, 0.7, 0.11, 0.08], [-0.66, 0.5, 0.075, 0.06]];
  for (var i = 0; i < craters.length; i++) {
    var k = craters[i];
    var cx = k[0] * R;
    var cy = k[1] * R;
    c.beginPath();
    c.ellipse(cx, cy, k[2] * R, k[3] * R, -0.3, 0, TAU);
    c.fillStyle = T.craterShade;
    c.fill();
    c.beginPath();
    c.ellipse(cx + k[2] * R * 0.16, cy + k[3] * R * 0.2, k[2] * R * 0.8, k[3] * R * 0.75, -0.3, 0, TAU);
    c.fillStyle = T.crater;
    c.fill();
  }

  // Glint on the fat of the crescent.
  c.fillStyle = "rgba(255,255,255,0.95)";
  c.beginPath();
  c.ellipse(-R * 0.5, -R * 0.66, R * 0.12, R * 0.06, -0.55, 0, TAU);
  c.fill();
  c.restore();

  // A thin blue-grey line along the terminator (the bite's arc inside the disc): the cel edge
  // between the lit crescent and the dark side.
  var w = R * MOON_TERMINATOR_LINE;
  c.save();
  c.beginPath();
  c.arc(0, 0, R, 0, TAU);
  c.clip();
  c.beginPath();
  c.arc(bx, by, br + w * 0.5, 0, TAU);
  c.lineWidth = w;
  c.strokeStyle = T.edge;
  c.stroke();
  c.restore();
}

function clipCrescent(c, R, bx, by, br) {
  c.beginPath();
  c.arc(0, 0, R, 0, TAU);
  c.clip();
  c.beginPath();
  c.rect(-R * 2, -R * 2, R * 4, R * 4);
  c.arc(bx, by, br, 0, TAU, true);
  c.clip("evenodd");
}

/**
 * Faint cool rim round the moon's dark side: [radius × R, alpha]; the dark disc sits inside it.
 * A pale blue-grey (not ink), so the unlit part of the disc is traced by a soft earthshine
 * edge rather than a dark ring.
 */
export var MOON_DARK_RIM = [1.035, 0.3];
export var MOON_DARK_RIM_RGB = [150, 172, 226];
export var MOON_DARK_FILL = 0.965;

/**
 * The dark side of the moon (the whole disc, under the crescent) in `rgb`, with a faint cool
 * rim. Drawn live in both renderers so it takes the colour of the sky behind the moon.
 * In Pixi it is two tinted lobe discs under the crescent sprite; this is the Canvas 2D twin.
 */
export function drawMoonDarkSide(c, x, y, R, rgb) {
  c.save();
  c.translate(x, y);
  var prev = c.globalAlpha;
  c.globalAlpha = prev * MOON_DARK_RIM[1];
  c.fillStyle = rgbString(MOON_DARK_RIM_RGB);
  c.beginPath();
  c.arc(0, 0, R * MOON_DARK_RIM[0], 0, TAU);
  c.fill();
  c.globalAlpha = prev;
  c.beginPath();
  c.arc(0, 0, R * MOON_DARK_FILL, 0, TAU);
  c.fillStyle = rgbString(rgb);
  c.fill();
  c.restore();
}

/** Colour of the moon's dark side for a given sky colour behind it: the sky, lifted a little. */
export function moonDarkRgb(skyRgb) {
  var s = skyRgb || [14, 26, 69];
  return [
    Math.round(s[0] + (96 - s[0]) * 0.22),
    Math.round(s[1] + (116 - s[1]) * 0.22),
    Math.round(s[2] + (178 - s[2]) * 0.22),
  ];
}

/**
 * Place the baked sun or moon (and its halo and rays) on a Canvas 2D context.
 * `pose` comes from `celestialPose` in render/sky/celestial.js.
 *
 * @param {CanvasRenderingContext2D} c
 * @param {{ kind: "sun"|"moon", x: number, y: number, r: number, sx: number, sy: number,
 *   rayRot: number, rayPulse: number, sunset: number, darkRgb?: number[], haloRgb: number[] }} pose
 * @param {number} dpr
 */
export function drawCelestialPose(c, pose, dpr) {
  if (!c || !pose || !(pose.r > 0)) return;
  var r = pose.r;
  var halo = pose.kind === "moon" ? MOON_HALO : SUN_HALO;
  c.save();
  c.translate(pose.x, pose.y);
  c.scale(pose.sx, pose.sy);
  var prevAlpha = c.globalAlpha;
  c.fillStyle = rgbString(pose.haloRgb);
  for (var i = halo.length - 1; i >= 0; i--) {
    c.globalAlpha = prevAlpha * halo[i][1] * (pose.haloAlpha != null ? pose.haloAlpha : 1);
    c.beginPath();
    c.arc(0, 0, r * halo[i][0], 0, TAU);
    c.fill();
  }
  c.globalAlpha = prevAlpha;
  if (pose.kind === "sun") {
    c.save();
    c.rotate(pose.rayRot || 0);
    c.scale(pose.rayPulse || 1, pose.rayPulse || 1);
    placeArt(c, getCelestialArt("rays", r, dpr), 1);
    if (pose.sunset > 0.01) placeArt(c, getCelestialArt("raysSet", r, dpr), pose.sunset);
    c.restore();
    placeArt(c, getCelestialArt("sun", r, dpr), 1);
    if (pose.sunset > 0.01) placeArt(c, getCelestialArt("sunSet", r, dpr), pose.sunset);
  } else {
    if (pose.darkRgb) drawMoonDarkSide(c, 0, 0, r, pose.darkRgb);
    placeArt(c, getCelestialArt("moon", r, dpr), 1);
  }
  c.restore();
}

function placeArt(c, art, alpha) {
  if (!art || !art.canvas || typeof c.drawImage !== "function") return;
  var prev = c.globalAlpha;
  if (alpha < 1) c.globalAlpha = prev * alpha;
  c.drawImage(art.canvas, -art.half, -art.half, art.half * 2, art.half * 2);
  c.globalAlpha = prev;
}

// ---------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------

function rgbString(rgb) {
  return "rgb(" + rgb[0] + "," + rgb[1] + "," + rgb[2] + ")";
}

function clamp01(v) {
  if (!(v > 0)) return 0;
  return v > 1 ? 1 : v;
}

function smooth01(v) {
  var x = clamp01(v);
  return x * x * (3 - 2 * x);
}

/** Mix two [r,g,b] colours. */
export function mixRgb(a, b, k) {
  var u = clamp01(k);
  return [
    Math.round(a[0] + (b[0] - a[0]) * u),
    Math.round(a[1] + (b[1] - a[1]) * u),
    Math.round(a[2] + (b[2] - a[2]) * u),
  ];
}
